import { AddSessionFormWrapper } from './styled'
import { ModalHeader, ModalCloseButton, IconButton, useDisclosure, Tooltip, useToast } from '@chakra-ui/react'
import { AddSessionFormContent, getMessageHandlerClass } from './AddSessionFormContent'
import { useForm, useWatch } from 'react-hook-form'
import { convertFormDataToSession, convertSessionToFormData, requestBoilerplate } from './formUtils'
import { useEffect, useMemo, useRef } from 'react'
import { isRouterMessageParser } from '../utils'
import { SessionType } from './SessionType'
import { YamlEditSessionForm } from './YamlSessionEditForm'
import { FiEdit, FiEye, FiServer } from 'react-icons/fi'
import { YamlView } from './YamlView'
import { capitalizeString } from '../../../fix-search/utils'

const shouldValidateAndDirty = { shouldValidate: true, shouldDirty: true }

const newMessageHandler = (messageHandlerClass, body = {}) => {
    if (messageHandlerClass === undefined) {
        return null
    }

    const messageHandler = {}
    messageHandler[`@${messageHandlerClass}`] = body
    return messageHandler
}

const base = (clazz) => `base${capitalizeString(clazz)}`

export const AddSessionForm = ({
    sessions,
    onFormSubmit,
    onClose,
    editSessionState,
    isAdvanced,
    onAdvancedChange,
    config,
    notify
}) => {
    const fixVersionHandlers = Object.fromEntries(
        Object.entries(config.fixVersionHandlers)
              .map(([fixVersion, handlers]) => [fixVersion, handlers['@FixVersionHandlers']])
    )
    const router = config.router?.['@UiRouterCfg']
    const sessionMessageProviders = config.sessionMessageProviders
    const defaultSessionParameters = config.defaultSessionParameters['@FixSessionCfg']

    const mappedClass = (handlerType, fixVersion) => {
        const clazz = fixVersionHandlers?.[fixVersion]?.[handlerType] ?? fixVersionHandlers?.[fixVersion]?.[base(handlerType)]
        return clazz ? newMessageHandler(clazz) : null
    }

    const editSession = editSessionState?.params
    const isActive = editSessionState?.isActive

    const defaultValues = useMemo(() => ({
        ...convertSessionToFormData(editSession ? editSession : defaultSessionParameters)
    }), [editSession, isAdvanced])

    const hookForm = useForm({
        defaultValues: defaultValues,
        mode: 'onChange'
    })
    const { control, watch, setValue, getValues, trigger, formState } = hookForm
    const { errors } = formState

    const disableWatch = useRef(false)
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (disableWatch.current) {
                return
            }

            switch (name) {
                case 'fixVersion':
                    const { fixVersion } = value
                    if (fixVersionHandlers) {
                        const messageParser = watch('messageParser')
                        const messageParserClass = getMessageHandlerClass(messageParser)
                        if (router && messageParserClass === router.messageParser) {
                            setValue(
                                'messageParser',
                                newMessageHandler(router.messageParser, {
                                    delegatingMessageParser: mappedClass('messageParser', fixVersion)
                                })
                            )
                        } else {
                            setValue(
                                'messageParser',
                                mappedClass('messageParser', fixVersion)
                            )
                        }
                        setValue(
                            'messageGenerator',
                            mappedClass('messageGenerator', fixVersion)
                        )
                        const mappedMessageNotifier = mappedClass('defaultMessageNotifier', fixVersion)
                        setValue('messageNotifier', mappedMessageNotifier ? mappedMessageNotifier : null)
                    }
                    break
                case 'senderCompID':
                case 'targetCompID':
                    if (!editSession) {
                        const sessionID = `${getValues('senderCompID')}:${getValues('targetCompID')}`
                        if (sessions.some(session => session.sessionID === sessionID)
                            || errors.senderCompID 
                            || errors.targetCompID) {
                            trigger(['senderCompID', 'targetCompID'])
                        }
                    }
                    break
                default:
                    return
            }
        })  
        return () => subscription.unsubscribe()
    }, [watch, fixVersionHandlers, setValue, getValues, errors, trigger])

     useEffect(() => {
         if (!editSession) {
             // For new sessions manually setValue for 'fixVersion' to force applying 'fixVersions' mapping
             const fixVersion = watch('fixVersion') || Object.keys(fixVersionHandlers)[0]
             setValue('fixVersion', fixVersion)
         }
     }, [])

    const toast = useToast()

    const onSubmit = (data) => {
        const request = requestBoilerplate(data)
        onFormSubmit(request)
            .catch(reason => {
                toast({
                    title: 'An error occurred.',
                    position: 'top',
                    description: 'Failed to add new FIX session.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true
                })
                console.log(reason)
            })
        onClose()
    }

    const isAcceptor = watch('connectionType') === 'acceptor'

    const handleSaveSchedule = (interval) => {
        setValue('sessionScheduleCfg', interval)
    }

    const sessionScheduleCfg = useWatch({
        name: 'sessionScheduleCfg',
        control,
        defaultValue: editSession ? editSession?.sessionScheduleCfg : defaultSessionParameters.sessionScheduleCfg
    })

    const consumer = useWatch({
        name: 'consumer',
        control,
        defaultValue: editSession ? editSession.consumer : defaultSessionParameters.consumer
    })

    const messageParser = useWatch({
        name: 'messageParser',
        control
    })

    const fixVersion = useWatch({
        name: 'fixVersion',
        control
    })

    const sessionType = 
        router && isRouterMessageParser(messageParser, router.messageParser)
            ? watch('routingDestination') ? SessionType.RouterDownstream : SessionType.RouterUpstream
            : SessionType.Simple

    const previousValues = useRef({})

    const setSessionType = (type) => {
        const previous = previousValues.current
        switch (type) {
            case SessionType.Simple:
                if (previous.messageParser !== null) {
                    setValue('messageParser', previous.messageParser)
                    previous.messageParser = null
                } else {
                    setValue('messageParser', mappedClass('messageParser', watch('fixVersion')))
                }
                
                if (previous.senderCompID !== undefined) {
                    setValue('senderCompID', previous.senderCompID, shouldValidateAndDirty)
                    previous.senderCompID = undefined
                } else {
                    setValue('senderCompID', undefined)
                }

                setValue('routingDestination', undefined)
                break
            case SessionType.RouterDownstream:
            case SessionType.RouterUpstream:
                const messageParser = watch('messageParser')
                const messageParserClass = getMessageHandlerClass(messageParser)
                if (messageParserClass !== router.messageParser) {
                    previous.messageParser = messageParser
                    setValue(
                        'messageParser', 
                        newMessageHandler(router.messageParser, {
                            delegatingMessageParser: mappedClass('messageParser', watch('fixVersion'))
                        })
                    )
                    trigger('messageParser')
                }

                const currentSenderCompID = watch('senderCompID')
                if (currentSenderCompID !== router.senderCompID) {
                    previous.senderCompID = currentSenderCompID
                    setValue('senderCompID', router.senderCompID, shouldValidateAndDirty)
                }

                const currentSenderSubID = watch('senderSubID')
                if (currentSenderSubID !== router.senderSubID) {
                    previous.senderSubID = currentSenderSubID
                    setValue('senderSubID', router.senderSubID, shouldValidateAndDirty)
                }

                setValue('routingDestination', type === SessionType.RouterDownstream ? true : undefined)
                break
            default:
                throw new Error('Unsupported type of session: ' + type)
        }
    }

    const handleEnableSearching = (enableSearching) => {
        setValue('consumer', enableSearching ? defaultSessionParameters.consumer : null)
    }

    const { isOpen: isYamlViewOpen, onClose: closeYamlView, onOpen: openYamlView } = useDisclosure()

    const handleRawSessionEdit = (editedSession) => {
        disableWatch.current = true
        closeYamlView()
        const formData = convertSessionToFormData(editedSession)

        Object.entries(formData)
            .forEach(([name, value]) => { 
                setValue(name, value)
            })

        // Clear values, which are not specified in edited session
        Object.entries(watch()) 
            .forEach(([name, value]) => {
                if (formData[name] === undefined && value) {
                    if (typeof value === 'string') {
                        setValue(name, '')
                    } else if (typeof value === 'boolean') {
                        setValue(name, false)
                    } else if (typeof value === 'object') {
                        if (name )
                        setValue(name, null)
                    }
                }
            })

        disableWatch.current = false
        trigger()
    }

    return (
        <AddSessionFormWrapper>
            <ModalHeader pl={0}>
                {isActive ? 'View' : editSession ? 'Edit' : 'Add'}
                {sessionType === SessionType.Simple
                    ? ''
                    : ' Router ' + (sessionType === SessionType.RouterDownstream ? 'Downstream' : 'Upstream')} Session{isActive ? ' Configuration' : ''}</ModalHeader>
            <ModalCloseButton />
            {!isYamlViewOpen &&
                <Tooltip
                    openDelay={500}
                    label={isActive ? 'View YAML configuration' : 'Edit YAML configuration'}
                >
                    <IconButton
                        position='absolute'
                        top={2}
                        right={12}
                        variant='ghost'
                        size='sm'
                        onClick={openYamlView}
                        aria-label='View YAML configuration'
                        icon={isActive ? <FiEye size={15}/> : <FiEdit size={15}/>}
                    />
                </Tooltip>
            }
            {(isYamlViewOpen && isActive) &&
                <Tooltip
                    openDelay={500}
                    label={'View form'}
                >
                    <IconButton
                        position='absolute'
                        top={2}
                        right={12}
                        variant='ghost'
                        size='sm'
                        onClick={closeYamlView}
                        aria-label='View form'
                        icon={<FiServer />}
                    />
                </Tooltip>
            }
            {!isYamlViewOpen &&
            <AddSessionFormContent
                sessions={sessions}
                editSessionState={editSessionState}
                defaultValues={defaultValues}
                onClose={onClose}
                advanced={isAdvanced}
                onSubmit={onSubmit}
                form={hookForm}
                fixVersion={fixVersion}
                fixVersionHandlers={fixVersionHandlers}
                sessionMessageProviders={sessionMessageProviders}
                isAcceptor={isAcceptor}
                handleSaveSchedule={handleSaveSchedule}
                intervals={sessionScheduleCfg}
                enableSearching={!!consumer}
                handleEnableSearching={handleEnableSearching}
                onAdvancedChange={onAdvancedChange}
                routerEnabled={router}
                sessionType={sessionType}
                setSessionType={setSessionType}
                notify={notify}
                />}
            {isYamlViewOpen && !isActive &&
                <YamlEditSessionForm 
                    session={convertFormDataToSession(watch())}
                    handleSessionEdit={handleRawSessionEdit}
                    discard={closeYamlView}
                    notify={notify}
                />
            }
            {isYamlViewOpen && isActive &&
                <YamlView
                    object={convertFormDataToSession(watch())}
                    closeView={closeYamlView}
                    notify={notify}
                />
            }
        </AddSessionFormWrapper>
    )
}
